import { z } from 'zod';
import {
    EMAIL_INPUT,
    FIRST_NAME_INPUT,
    MESSAGE_INPUT,
    NAME_INPUT,
    PLACE_INPUT,
} from '@modules/validation/util/form';
import { parsePhone } from '@modules/root/util/phone';

/**
 * Builds a phone-number schema that validates against the user's market
 * (NZ/AU) using libphonenumber-js via {@link parsePhone}. Unlike the shared
 * `PHONE_NUMBER_INPUT` (which only checks character shape), this rejects
 * numbers that aren't a real, dialable number for the given country, giving
 * the user immediate feedback before they submit.
 */
const makeEnquiryPhoneInput = (country?: string) =>
    z
        .string({ required_error: 'Phone number is required' })
        .trim()
        .min(1, { message: 'Phone number is required' })
        .refine((value) => parsePhone(value, country) !== null, {
            message: 'Please enter a valid phone number',
        });

const getEnquiryFormSchemaBase = (country?: string) =>
    z.object({
        name: NAME_INPUT,
        email: EMAIL_INPUT,
        phoneNumber: makeEnquiryPhoneInput(country),
        postcode: z.object(
            {
                header: z.string(),
                value: PLACE_INPUT,
            },
            {
                required_error: 'Suburb / Postcode is required',
                invalid_type_error: 'Suburb / Postcode is required',
            },
        ),
        branchId: z.string().optional(),
        buildHubId: z.string().optional(),
        message: MESSAGE_INPUT,
    });

/**
 * Get the validation schema for the enquiry form.
 * @param fullName Require a full name for the name field. Defaults to `true`.
 * @param includeBranch Include the branch field in the form. Defaults to `false`.
 * @param country The user's market country code (e.g. `NZ`, `AU`) used to
 * validate the phone number. When omitted, only international-format numbers
 * (with a `+` prefix) can be validated.
 * @returns The enquiry form Zod schema.
 */
export function getEnquiryFormSchema(
    fullName: boolean = true,
    includeBranch: boolean = false,
    country?: string,
) {
    let formSchema = getEnquiryFormSchemaBase(country);
    if (!fullName) {
        formSchema = formSchema.extend({ name: FIRST_NAME_INPUT });
    }
    if (includeBranch) {
        return formSchema.extend({
            branchId: z.string({ required_error: 'Branch is required' }),
        });
    }
    return formSchema;
}
